import { type EditorView } from 'prosemirror-view'
import { Unsubscribe } from 'redux'
import { QueryClient, QueryClientProvider } from 'react-query'
import { reaction } from 'mobx'
import { getTodosPluginState, TODOS_PLUGIN_KEY } from './constants'
import { State, ReactViewProps } from './Types'
import store from 'redux/store'
import { findAllTasksMarks } from './utils'
import { CollabItemInlineTooltip } from 'components/CollabItemInlineTooltip/CollabItemInlineTooltip'
import { Provider } from 'react-redux'
import { Todo } from 'api/clients/TodoClient'
import { inline, offset, shift, flip, hide, autoUpdate, computePosition } from '@floating-ui/dom'
import { observer } from 'mobx-react-lite'
import scrollIntoView from 'scroll-into-view-if-needed'
import { createRoot, Root } from 'react-dom/client'

const ReactView = observer(({ queryClient, docStore }: ReactViewProps) => {
  const todo = docStore.rootStore.todos.highlighted
  const userId = todo?.assignee?.id

  return (
    <Provider store={store}>
      <QueryClientProvider client={queryClient}>
        {userId && <CollabItemInlineTooltip userId={userId} content={todo.title} />}
      </QueryClientProvider>
    </Provider>
  )
})

export class TodosView {
  view: EditorView
  reduxUnsubscribe: Unsubscribe
  disposers: Array<() => void> = []
  queryClient: QueryClient

  /**
   * Tooltip container
   */
  widget: HTMLElement
  /**
   * Tooltip anchor
   */
  anchor: Element | null = null
  /**
   * Whether the floating ui has been initialised
   */
  initialized: boolean = false
  /**
   * Callback to clean up floating ui listeners
   */
  cleanupFloating?: () => void
  root: Root

  constructor(view: EditorView) {
    this.view = view
    this.reduxUnsubscribe = store.subscribe(() => {
      view.dispatch(view.state.tr)
    })

    const { queryClient }: State = getTodosPluginState(view.state)
    this.queryClient = queryClient

    this.setupReactions()

    this.widget = document.createElement('div')
    this.widget.style.position = 'absolute'
    this.widget.style.display = 'none'
    this.root = createRoot(this.widget)
    this.addWidgetToDom()
  }

  get enabled() {
    return getTodosPluginState(this.view.state).enabled
  }

  get docStore() {
    return getTodosPluginState(this.view.state).docStore
  }

  setupReactions = () => {
    this.disposers = [
      reaction(() => this.docStore?.docMode, this.handleDocModeChange),
      reaction(() => this.docStore?.rootStore.todos.selected, this.handleSelect),
      reaction(() => this.docStore?.rootStore.todos.highlighted, this.handleHighlight),
      reaction(
        () => this.docStore?.rootStore.todos,
        () => this.view.dispatch(this.view.state.tr),
      ),
    ]
  }

  handleDocModeChange = () => {
    const tr = this.view.state.tr
    tr.setMeta(TODOS_PLUGIN_KEY, { enabled: true })
    this.view.dispatch(tr)
  }

  handleSelect = () => {
    if (!this.enabled) return

    const todo = this.docStore?.rootStore.todos.selected
    if (todo && todo.uuid) {
      const anchor = findAllTasksMarks(todo.uuid)[0]
      scrollIntoView(anchor, { block: 'center', inline: 'nearest', behavior: 'smooth' })
    }
    this.view.dispatch(this.view.state.tr)
  }

  handleHighlight = () => {
    if (!this.enabled) return

    if (this.docStore?.rootStore.todos.highlighted) {
      this.showTooltip(this.docStore?.rootStore.todos.highlighted)
    } else {
      this.hideTooltip()
    }
    this.view.dispatch(this.view.state.tr)
  }

  addWidgetToDom() {
    if (!this.docStore) {
      return
    }
    if (this.view.dom.parentNode) {
      this.view.dom.parentNode.appendChild(this.widget)
      this.root.render(<ReactView queryClient={this.queryClient} docStore={this.docStore} />)
      this.initialized = true
    }
  }

  hideTooltip() {
    this.widget.style.display = 'none'
    this.cleanupFloating?.()
    this.cleanupFloating = undefined
  }

  showTooltip(todo: Todo) {
    this.cleanupFloating?.()
    this.cleanupFloating = undefined

    const anchor = findAllTasksMarks(todo.uuid)[0]
    if (this.anchor !== anchor) {
      this.anchor = anchor
    }
    if (!this.anchor) {
      this.hideTooltip()
      return
    }

    this.widget.style.display = 'block'

    if (this.cleanupFloating || !this.anchor) {
      return
    }

    this.cleanupFloating = autoUpdate(
      this.anchor,
      this.widget,
      () => {
        if (!this.anchor) {
          return
        }
        computePosition(this.anchor, this.widget, {
          placement: 'top',
          middleware: [inline(), offset(2), flip(), shift(), hide({ strategy: 'escaped', padding: 40 })],
        }).then(({ x, y, middlewareData }) => {
          Object.assign(this.widget.style, {
            top: '0',
            left: '0',
            transform: `translate(${Math.round(x)}px,${Math.round(y)}px)`,
            // TODO: Fix this the next time the file is edited.
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            display: middlewareData.hide!.escaped ? 'none' : 'block',
            zIndex: 1,
          })
        })
      },
      {
        animationFrame: true,
      },
    )
  }

  update(view: EditorView) {
    this.view = view
    if (!this.initialized) {
      this.addWidgetToDom()
    }
  }

  destroy() {
    this.reduxUnsubscribe()
    this.disposers.forEach((dispose) => dispose())
    setTimeout(() => this.root.unmount(), 0)
    this.cleanupFloating?.()
    this.widget.remove()
  }
}
